import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { createAppTheme } from './theme';
import Layout from './components/layout/ModernLayout';
import LandingNew from './pages/LandingNew';
import FAQ from './pages/FAQ';
import Reforme2026 from './pages/Reforme2026';
import FacturationBtp from './pages/FacturationBtp';
import FacturationBeaute from './pages/FacturationBeaute';
import FacturationArtisan from './pages/FacturationArtisan';
import FacturationFreelance from './pages/FacturationFreelance';
import FacturationPME from './pages/FacturationPME';
import LogicielDevisFactureArtisan from './pages/LogicielDevisFactureArtisan';
import LogicielFactureAutoEntrepreneur from './pages/LogicielFactureAutoEntrepreneur';
import LogicielFacturePME from './pages/LogicielFacturePME';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import CompanyProfile from './pages/CompanyProfile';
import Companies from './pages/Companies';
import Quotes from './pages/Quotes';
import Invoices from './pages/Invoices';
import Settings from './pages/Settings';
import ExternalDocuments from './pages/ExternalDocuments';
import Accounting from './pages/Accounting';
import AccountingReports from './pages/AccountingReports';
import ExpenseNotes from './pages/ExpenseNotes';
import Pricing from './pages/Pricing';
import Subscription from './pages/Subscription';
import Terms from './pages/Terms';
import LegalMentions from './pages/LegalMentions';
import Contact from './pages/Contact';
import AppFooter from './components/AppFooter';
import PageViewTracker from './components/PageViewTracker';
import AdminStats from './pages/AdminStats';
import TeamMembers from './pages/TeamMembers';
import { clearCompanyProfile, loadCompanyProfile, saveCompanyProfile } from './utils/companyProfileStorage';
import { authService } from './services/api';
import ActivityOnboardingDialog from './components/ActivityOnboardingDialog';
import { loadActivityMode } from './utils/activityModeStorage';
import { LanguageProvider } from './context/LanguageContext';

// Authentification simplifiée (cookie HttpOnly côté serveur)
const useSimpleAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const isLoggedInHint = localStorage.getItem('isLoggedIn') === 'true';
    return isLoggedInHint ? null : false;
  });

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      try {
        await authService.getCurrentUser();
        if (cancelled) return;
        localStorage.setItem('isLoggedIn', 'true');
        setIsAuthenticated(true);
      } catch {
        if (cancelled) return;
        localStorage.removeItem('isLoggedIn');
        setIsAuthenticated(false);
      }
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = () => {
    localStorage.setItem('isLoggedIn', 'true');
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearCompanyProfile();
    setIsAuthenticated(false);

    authService.logoutRequest().catch(() => {
      // best-effort
    });
  };

  return { isAuthenticated, login, logout };
};

const getAppSettingsStorageKey = () => {
  const user = authService.getCurrentUserFromStorage();
  const identifier = user?.id || user?.email;
  return identifier ? `appSettings:${identifier}` : 'appSettings';
};

const readInactivityConfig = () => {
  const key = getAppSettingsStorageKey();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return { lockAfterInactivity: true, inactivityMinutes: 15 };
    }
    const parsed = JSON.parse(raw);
    const lockAfterInactivity = Boolean(parsed?.securitySettings?.lockAfterInactivity);
    const minutes = Number(parsed?.securitySettings?.inactivityMinutes);
    const inactivityMinutes = Number.isFinite(minutes) ? Math.min(240, Math.max(1, minutes)) : 15;
    return { lockAfterInactivity, inactivityMinutes };
  } catch {
    return { lockAfterInactivity: true, inactivityMinutes: 15 };
  }
};

function App() {
  const { isAuthenticated, login, logout } = useSimpleAuth();

  const [activityOnboardingOpen, setActivityOnboardingOpen] = useState(false);

  // Déconnexion auto après inactivité (configurable via Paramètres)
  const inactivityTimerRef = useRef(null);
  const inactivityConfigRef = useRef({ lockAfterInactivity: true, inactivityMinutes: 15 });

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const reloadInactivityConfig = useCallback(() => {
    inactivityConfigRef.current = readInactivityConfig();
  }, []);

  const scheduleInactivityLogout = useCallback(() => {
    clearInactivityTimer();
    const { lockAfterInactivity, inactivityMinutes } = inactivityConfigRef.current;
    if (!lockAfterInactivity) return;
    inactivityTimerRef.current = setTimeout(() => {
      logout();
      window.location.href = '/login';
    }, inactivityMinutes * 60 * 1000);
  }, [clearInactivityTimer, logout]);

  // Migration soft: remplacer les anciens oranges/copper enregistrés par le taupe
  useEffect(() => {
    try {
      const profile = loadCompanyProfile();
      if (!profile?.primaryColor) return;
      const normalized = String(profile.primaryColor).trim().toUpperCase();
      const legacyOranges = new Set(['#FF9800', '#F59E0B', '#D97706', '#C07B3B', '#B87333']);
      if (!legacyOranges.has(normalized)) return;

      const updated = {
        ...profile,
        primaryColor: '#977F6B',
        secondaryColor: profile.secondaryColor || '#5E564F',
      };
      saveCompanyProfile(updated);
      window.dispatchEvent(new Event('companyProfileUpdated'));
    } catch {
      // best-effort
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setActivityOnboardingOpen(false);
      return;
    }

    try {
      const existing = loadActivityMode();
      setActivityOnboardingOpen(!existing?.id);
    } catch {
      setActivityOnboardingOpen(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearInactivityTimer();
      return;
    }

    reloadInactivityConfig();
    scheduleInactivityLogout();

    const onActivity = () => scheduleInactivityLogout();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleInactivityLogout();
      }
    };
    const onSettingsUpdated = () => {
      reloadInactivityConfig();
      scheduleInactivityLogout();
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    for (const eventName of activityEvents) {
      window.addEventListener(eventName, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('appSettingsUpdated', onSettingsUpdated);

    return () => {
      for (const eventName of activityEvents) {
        window.removeEventListener(eventName, onActivity);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('appSettingsUpdated', onSettingsUpdated);
      clearInactivityTimer();
    };
  }, [
    isAuthenticated,
    clearInactivityTimer,
    reloadInactivityConfig,
    scheduleInactivityLogout,
  ]);

  const getThemeColors = () => {
    const profile = loadCompanyProfile();
    return {
      primary: profile?.primaryColor || '#977F6B',
      secondary: profile?.secondaryColor || '#5E564F'
    };
  };

  const [themeColors, setThemeColors] = useState(getThemeColors);

  useEffect(() => {
    const handleThemeUpdate = () => {
      setThemeColors(getThemeColors());
    };

    window.addEventListener('companyProfileUpdated', handleThemeUpdate);
    window.addEventListener('storage', handleThemeUpdate);

    return () => {
      window.removeEventListener('companyProfileUpdated', handleThemeUpdate);
      window.removeEventListener('storage', handleThemeUpdate);
    };
  }, []);

  const dynamicTheme = useMemo(
    () => createAppTheme(themeColors.primary, themeColors.secondary),
    [themeColors]
  );

  // Bootstrap en cours
  if (isAuthenticated === null) {
    return (
      <ThemeProvider theme={dynamicTheme}>
        <CssBaseline />
        <div style={{ minHeight: '100vh' }} />
      </ThemeProvider>
    );
  }

  // Si non authentifié, afficher les pages publiques
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={dynamicTheme}>
        <CssBaseline />
        <Router>
          <PageViewTracker />
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1 }}>
              <Routes>
                <Route path="/" element={<LandingNew />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/reforme-2026" element={<Reforme2026 />} />
                <Route path="/facturation-btp" element={<FacturationBtp />} />
                <Route path="/facturation-beaute" element={<FacturationBeaute />} />
                <Route path="/facturation-artisan" element={<FacturationArtisan />} />
                <Route path="/facturation-freelance" element={<FacturationFreelance />} />
                <Route path="/facturation-pme" element={<FacturationPME />} />
                <Route path="/logiciel-devis-facture-artisan" element={<LogicielDevisFactureArtisan />} />
                <Route path="/logiciel-facture-auto-entrepreneur" element={<LogicielFactureAutoEntrepreneur />} />
                <Route path="/logiciel-facture-pme" element={<LogicielFacturePME />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/conditions-generales" element={<Terms />} />
                <Route path="/mentions-legales" element={<LegalMentions />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/register" element={<Register onRegister={login} />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/login" element={<Login onLogin={login} />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
            <AppFooter />
          </div>
        </Router>
      </ThemeProvider>
    );
  }

  // Si authentifié, afficher l'application avec le Layout
  return (
    <LanguageProvider>
      <ThemeProvider theme={dynamicTheme}>
      <CssBaseline />
      <Router>
        <PageViewTracker />
        <Layout onLogout={logout}>
          <ActivityOnboardingDialog
            open={activityOnboardingOpen}
            onClose={() => setActivityOnboardingOpen(false)}
          />
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin/stats" element={<AdminStats />} />
            <Route path="/company-profile" element={<CompanyProfile />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/quotes" element={<Quotes />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/accounting" element={<Accounting />} />
            <Route path="/accounting-reports" element={<AccountingReports />} />
            <Route path="/expense-notes" element={<ExpenseNotes />} />
            <Route path="/external-documents" element={<ExternalDocuments />} />
            <Route path="/facturation-btp" element={<FacturationBtp />} />
            <Route path="/facturation-beaute" element={<FacturationBeaute />} />
            <Route path="/facturation-artisan" element={<FacturationArtisan />} />
            <Route path="/facturation-freelance" element={<FacturationFreelance />} />
            <Route path="/facturation-pme" element={<FacturationPME />} />
            <Route path="/logiciel-devis-facture-artisan" element={<LogicielDevisFactureArtisan />} />
            <Route path="/logiciel-facture-auto-entrepreneur" element={<LogicielFactureAutoEntrepreneur />} />
            <Route path="/logiciel-facture-pme" element={<LogicielFacturePME />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/conditions-generales" element={<Terms />} />
            <Route path="/mentions-legales" element={<LegalMentions />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/team" element={<TeamMembers />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Layout>
      </Router>
    </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
